import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus } from '../utils/EventBus';
/** Navigation target meaning "leave the level editor entirely". */
export const BACK_SENTINEL = '__back__';
/**
 * Unsaved-changes guard for one embedded editor inside LevelEditor.
 *
 * Encapsulates the dirty flag, the entity name shown in the confirmation
 * modal, the deferred navigation target, and the save-function registration
 * that editors push upward. LevelEditor previously hand-copied this state
 * machine once per editor; it now instantiates this hook per editor instead.
 */
export function useEditorUnsavedGuard({ tab, savedEvent, activeTab, navigate }) {
    const [hasUnsaved, setHasUnsaved] = useState(false);
    const [unsavedName, setUnsavedName] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [pendingTarget, setPendingTarget] = useState(null);
    const saveFnRef = useRef(null);
    // After a successful save, continue the navigation that opened the modal.
    useEffect(() => {
        return EventBus.on(savedEvent, () => {
            if (pendingTarget) {
                setHasUnsaved(false);
                navigate(pendingTarget);
                setPendingTarget(null);
            }
        });
    }, [savedEvent, pendingTarget, navigate]);
    const reportUnsaved = useCallback((unsaved, name) => {
        setHasUnsaved(unsaved);
        setUnsavedName(name);
    }, []);
    const registerSave = useCallback((fn) => {
        saveFnRef.current = fn;
    }, []);
    const intercept = useCallback((targetTab) => {
        if (activeTab === tab && targetTab !== tab && hasUnsaved) {
            setPendingTarget(targetTab);
            setModalOpen(true);
            return true;
        }
        return false;
    }, [activeTab, tab, hasUnsaved]);
    const confirmSave = useCallback(() => {
        var _a;
        (_a = saveFnRef.current) === null || _a === void 0 ? void 0 : _a.call(saveFnRef);
        setModalOpen(false);
    }, []);
    const discard = useCallback(() => {
        setModalOpen(false);
        setHasUnsaved(false);
        if (pendingTarget) {
            navigate(pendingTarget);
            setPendingTarget(null);
        }
    }, [pendingTarget, navigate]);
    return { hasUnsaved, unsavedName, isModalOpen, reportUnsaved, registerSave, intercept, confirmSave, discard };
}
